import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, Loader, AlertTriangle, X } from 'lucide-react';
import { useConnectionStatus } from '../services/ConnectionService';
import { useSocket } from '../context/SocketContext';

interface ConnectionStatusProps {
  className?: string;
  showText?: boolean;
}

const ConnectionStatus: React.FC<ConnectionStatusProps> = ({ className = '', showText = true }) => {
  const { t } = useTranslation('common');
  const { status, isOnline, forceReconnect } = useConnectionStatus();
  const { isConnected: isSocketConnected } = useSocket();
  const [showDetailedNotification, setShowDetailedNotification] = useState(false);
  const [notificationTimeout, setNotificationTimeout] = useState<NodeJS.Timeout | null>(null);

  // Déterminer un état global (Network + Socket)
  const isFullyConnected = isOnline && isSocketConnected;
  const isPartiallyConnected = isOnline && !isSocketConnected;

  useEffect(() => {
    if (status === 'error' || status === 'offline' || isPartiallyConnected) {
      // Délai de grâce de 3s pour éviter de flasher l'utilisateur au chargement
      const graceTimeout = setTimeout(() => {
        setShowDetailedNotification(true);
      }, 3000);
      
      const hideTimeout = setTimeout(() => {
        setShowDetailedNotification(false);
      }, 8000); // 3s grace + 5s d'affichage
      
      setNotificationTimeout(hideTimeout);
      return () => {
        clearTimeout(graceTimeout);
        clearTimeout(hideTimeout);
      };
    }
    
    if (isFullyConnected) {
      setShowDetailedNotification(false);
    }
  }, [status, isPartiallyConnected, isFullyConnected]);

  const getStatusConfig = () => {
    if (isPartiallyConnected) {
      return {
        icon: <Loader size={16} className="animate-spin" />,
        text: 'Sync...',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        title: 'Connexion réseau OK, synchronisation en cours',
        detailedMessage: 'Le réseau est disponible, mais la liaison temps réel (Socket) est en cours de rétablissement.',
      };
    }

    switch (status) {
      case 'online':
        return {
          icon: <Wifi size={16} />,
          text: 'Connecté',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          title: 'Connexion stable',
          detailedMessage: 'Vous êtes connecté et toutes les fonctionnalités sont disponibles.',
        };
      case 'reconnecting':
        return {
          icon: <Loader size={16} className="animate-spin" />,
          text: 'Reconnexion...',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          title: 'Reconnexion en cours',
          detailedMessage: 'Tentative de reconnexion au serveur. Les données peuvent être temporairement indisponibles.',
        };
      case 'offline':
        return {
          icon: <WifiOff size={16} />,
          text: 'Hors ligne',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          title: 'Vous êtes hors ligne',
          detailedMessage: 'Veuillez vérifier votre connexion Internet.',
        };
      case 'error':
        return {
          icon: <AlertTriangle size={16} />,
          text: 'Erreur',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          title: 'Erreur de connexion',
          detailedMessage: 'Impossible de joindre le serveur. Vérifiez votre connexion.',
        };
      default:
        return {
          icon: <Wifi size={16} />,
          text: '?',
          color: 'text-slate-600',
          bgColor: 'bg-slate-50',
          borderColor: 'border-slate-200',
          title: 'Statut inconnu',
          detailedMessage: 'Le statut de connexion n\'est pas disponible.',
        };
    }
  };

  const config = getStatusConfig();

  return (
    <>
      <div 
        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border transition-all cursor-pointer hover:opacity-80 ${config.bgColor} ${config.borderColor} ${config.color} ${className}`}
        onClick={() => !isFullyConnected && forceReconnect()}
        title={config.title}
      >
        {config.icon}
        {showText && <span className="text-sm font-medium">{config.text}</span>}
      </div>

      {showDetailedNotification && (status !== 'online' || isPartiallyConnected) && (
        <div className={`fixed bottom-4 right-4 max-w-sm rounded-lg shadow-lg border-l-4 p-4 ${config.bgColor} ${config.borderColor} z-50 animate-in fade-in slide-in-from-bottom-4`}>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 ${config.color}`}>{config.icon}</div>
              <div>
                <h3 className={`font-bold ${config.color}`}>{config.title}</h3>
                <p className="text-sm text-slate-600 mt-1">{config.detailedMessage}</p>
              </div>
            </div>
            <button
              onClick={() => setShowDetailedNotification(false)}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ConnectionStatus;
